import { useState, useEffect, useMemo } from 'react'
import { useParams } from 'react-router'
import { api } from '../api/client'
import { useEntities } from '../context/EntitiesContext'
import { PromptPageHeader } from '../components/PromptPageHeader'
import { PromptPageNavigation } from '../components/PromptPageNavigation'
import { DatasetDetail } from './dataset/DatasetDetail'

export function DatasetPage () {
  const { id } = useParams<{ id: string }>()
  const { prompts, folders, datasets: allDatasets, loading: entitiesLoading, refreshDatasets } = useEntities()
  const prompt = id ? prompts.find(p => p.id === id) || null : null
  const currentFolder = prompt?.folderId ? folders.find(f => f.id === prompt.folderId) : null

  const datasets = useMemo(() => id ? allDatasets.filter(d => d.promptId === id) : [],
    [allDatasets, id],
  )

  const [selectedDatasetId, setSelectedDatasetId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    if (entitiesLoading) return

    if (datasets.length > 0) {
      setSelectedDatasetId(prev => {
        if (prev && datasets.some(d => d.id === prev)) return prev
        return datasets[0].id
      })
    } else {
      setSelectedDatasetId(null)
    }
  }, [datasets, entitiesLoading])

  const handleCreateDataset = async () => {
    if (!id) return
    setCreating(true)
    setError(null)
    try {
      if (!prompt) return

      const baseName = `${prompt.name} - dataset`
      let name = baseName
      let i = 1
      while (datasets.some(d => d.name === name)) {
        i++
        name = `${baseName} ${i}`
      }

      const newDataset = await api.datasets.create({
        name,
        promptId: prompt.id,
        promptVariantIds: [id],
      })
      await refreshDatasets()
      setSelectedDatasetId(newDataset.id)
    } catch {
      setError('Failed to create dataset')
    } finally {
      setCreating(false)
    }
  }

  const selectedDataset = datasets.find(d => d.id === selectedDatasetId) || null

  if (!prompt) {
    return (
      <div>
        <div className="text-gray-400">Loading...</div>
      </div>
    )
  }

  return (
    <div>
      <PromptPageHeader prompt={prompt} currentFolder={currentFolder} />
      <PromptPageNavigation promptId={id!} />

      {error && <div className="alert-error mb-4">{error}</div>}

      {/* Selector */}
      <div className="mb-4 flex gap-4 items-center">
        <div className="flex-1 max-w-xs">
          <label className="input-label">Dataset</label>
          <select
            value={selectedDatasetId || ''}
            onChange={e => setSelectedDatasetId(e.target.value)}
            className="input"
            disabled={datasets.length === 0}
          >
            {datasets.length === 0
              ? (
              <option value="">No datasets</option>
                )
              : (
                  datasets.map(d => <option key={d.id} value={d.id}>{d.name}</option>)
                )}
          </select>
        </div>
        <div className="pt-6">
          <button onClick={handleCreateDataset} disabled={creating} className="btn btn-secondary">
            {creating ? 'Creating...' : '+ New Dataset'}
          </button>
        </div>
      </div>

      {/* Detail View */}
      {selectedDataset
        ? (
        <DatasetDetail
          key={selectedDataset.id}
          dataset={selectedDataset}
          onUpdate={() => refreshDatasets()}
          onDelete={() => refreshDatasets()}
        />
          )
        : (
        <div className="text-gray-400">
          {entitiesLoading ? 'Loading datasets...' : 'Select or create a dataset to manage items.'}
        </div>
          )}
    </div>
  )
}
